import * as React from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import { DefaultTheme } from "styled-components";
import cronstrue from "cronstrue";
import ReactJson from "react-json-view";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { ICombinedJobSchedule } from "src/core/process-job/api/processJobsSchedule.api";
import { Text } from "src/library/components/Text";

export interface ICombinedJobScheduleColumn {
  //buttons for each schedule
  actionButton: (info: CellContext<ICombinedJobSchedule, "unknown">) => React.ReactNode,
  terminology?: ITerminology,
  //ability to choose columns outside of this file
  columns?: ProcessJobScheduleColumnsPicked[]
  theme: DefaultTheme
}

type ProcessJobScheduleColumnsPicked = keyof AvailableProcessJobScheduleColumns;

interface AvailableProcessJobScheduleColumns{
  id: ColumnDef<ICombinedJobSchedule, unknown>,
  updatedAt: ColumnDef<ICombinedJobSchedule, unknown>,
  schedule: ColumnDef<ICombinedJobSchedule, unknown>,
  name: ColumnDef<ICombinedJobSchedule, unknown>,
  description: ColumnDef<ICombinedJobSchedule, unknown>,
  jobName: ColumnDef<ICombinedJobSchedule, unknown>,
  jobParams: ColumnDef<ICombinedJobSchedule, unknown>,
  status: ColumnDef<ICombinedJobSchedule, unknown>
  actions: ColumnDef<ICombinedJobSchedule, unknown>,
}

/** columns for process job schedules*/
export const getProcessJobScheduleColumns = (params: ICombinedJobScheduleColumn) => {
  const customColumnHelper = customCreateColumnHelper<ICombinedJobSchedule>();
  const ColumnHelper = createColumnHelper<ICombinedJobSchedule>();

  const AllColumns: AvailableProcessJobScheduleColumns = {
    id: customColumnHelper.numberColumn(ColumnHelper, "id", {
      header: "ID",
      size: 50
    }),
    name: customColumnHelper.standardTextColumn(ColumnHelper, "name", { header: "Name" }),
    //uses a Cron Describer to show human readable schedule
    schedule: customColumnHelper.standardTextColumn(ColumnHelper, "scheduleExpression", {
      header: "Schedule",
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const cellValue = info.row.original.scheduleExpression;
        const isRateType = cellValue?.split("(")[ 0 ] === "rate";
        let filteredExpression;

        if (!cellValue) {
          return (
            <Text color="error">
              Error, Please inform your admin of this error
            </Text>
          );
        }

        if (!isRateType) {
          filteredExpression = cellValue.replace("cron(", "").replace(")", "");
        } else {
          filteredExpression = cellValue.replace("rate(", "").replace(")", "");
        }

        const humanReadable = !isRateType ? cronstrue.toString(filteredExpression, {
          verbose: true,
          dayOfWeekStartIndexZero: false,
          monthStartIndexZero: false,
          use24HourTimeFormat: true
        }) : `every ${filteredExpression}s`;

        const cell = (
          <Text as="h4">
            {humanReadable}
          </Text>
        );

        return cell;
      }
    }),
    description: customColumnHelper.standardTextColumn(ColumnHelper, "description", { header: "Description" }),
    jobName: customColumnHelper.standardTextColumn(ColumnHelper, "jobName", {
      header: "Process Name",
      cell: info => {
        return (
          <Text>
            {info.row.original.jobName.replaceAll("-", " ")}
          </Text>
        );
      }
    }),
    //uses a library to show json data
    jobParams: customColumnHelper.standardTextColumn(ColumnHelper, "jobParams", {
      header: "Settings",
      enableSorting: false,
      enableColumnFilter: false,
      size: 300,
      cell: info => {
        return (
          <ReactJson
            src={info.row.original.jobParams}
            name={false}
            enableClipboard={false}
            displayDataTypes={false}
            collapsed
            style={{ fontSize: "12px" }}
            indentWidth={1}
          />
        );
      }
    }),

    //displays enable / suspended based on AWS ENABLED/DISABLED
    status: customColumnHelper.standardTextColumn(ColumnHelper, "state", {
      header: "Status",
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      meta: { style: { align: "center" } },
      cell: info => {
        switch (info.cell.getValue()) {
          case "ENABLED":
            return (
              <Text
                style={{ color: params.theme.colors.success }}
                fontSize="h4"
              >
                Enabled
              </Text>
            );
          case "DISABLED":
            return (
              <Text
                color="error"
                fontSize="h4"
              >
                Suspended
              </Text>
            );

          default:
            return (
              <Text color="error">
                Error, Please inform your system administrator of this
              </Text>
            );
        }
      }
    }),
    updatedAt: customColumnHelper.dateColumn(ColumnHelper, "updatedAt", { header: "Updated At" }),

    actions: customColumnHelper.actionColumn(ColumnHelper, params.actionButton, {})
  };

  if (params.columns) {
    return params.columns.map(column => AllColumns[ column ]);
  } else {
    return Object.values(AllColumns);
  }
};
